import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertCircle, CheckCircle2, ExternalLink, Layers, Loader2, RotateCcw, Square, Upload, X } from 'lucide-react';
import { supabase } from '../../src/lib/supabase';
import {
  chaveUcMes, chavesComFatura, emFila, identificarAgrupamento, interpretarRespostaRobo, resultadoDoEnvio, resumoDoEnvio,
  rotuloPaginas, ucsForaDoBordero,
  type PaginaBordero, type ResultadoEnvio, type SeparacaoBordero,
} from '../../src/lib/importacaoBordero.ts';

// Importação do borderô de agrupamento (#6208 / 6208.06). Abre pela tela de Agrupamentos de
// Faturamento.
//
// O .7z (ou o PDF com várias faturas) vai para a edge `separar-bordero`, que devolve uma
// fatura por página e o destino de cada uma. As páginas "lançar" seguem, uma a uma, pelo
// MESMO webhook do "Importar fatura em PDF" (Robô Manual); as de acompanhamento ficam só
// listadas (UCs fora do rateio da Vigor — decisão da 6208.03; a leitura delas é a 6208.15).
//
// Depois de separar, a tela diz de qual agrupamento é o arquivo (pelo cadastro das UCs) e quais
// UCs dele não vieram: é o que separa "o envio falhou" de "a fatura não está no borderô".

const ENVIOS_SIMULTANEOS = 3;
const TEMPO_MAXIMO_ROBO_MS = 180_000;
const MAX_AUSENTES_NA_TELA = 8;

type Etapa = 'escolher' | 'separando' | 'previa' | 'lancando' | 'concluido';

interface UcDoAgrupamento {
  numero_uc: string;
  outro_numero_uc?: string | null;
}

interface Props {
  isOpen: boolean;
  arquivoInicial?: File | null;
  /** Agrupamentos da tela: dão o código na concessionária do que for identificado. */
  agrupamentos?: { id: number; codigos?: string[] }[];
  /** UCs de um agrupamento, para apontar as que não vieram no arquivo. */
  ucsDoAgrupamento?: (agrupamentoId: number) => UcDoAgrupamento[];
  onClose: () => void;
  onConcluido?: () => void;
}

/** UC do agrupamento identificado que não tem página no arquivo. */
interface UcAusente {
  numero: string;
  comFatura: boolean;
}

const normUc = (v: string | null | undefined) => String(v ?? '').replace(/\D/g, '').replace(/^0+/, '');

const reais = (v: number | null) =>
  v === null ? '—' : v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const ehArquivoAceito = (f: File) => /\.(7z|pdf)$/i.test(f.name) || f.type === 'application/pdf';

const ROTULO_DESTINO: Record<PaginaBordero['destino'], { texto: string; classe: string }> = {
  lancar: { texto: 'Lançar', classe: 'bg-primary/10 text-primary dark:bg-primary/20 dark:text-primary-light' },
  acompanhamento: { texto: 'Acompanhar', classe: 'bg-secondary/15 text-amber-700 dark:text-secondary' },
  revisar: { texto: 'Revisar', classe: 'bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-400' },
};

const ROTULO_ENVIO: Record<ResultadoEnvio['estado'], string> = {
  aguardando: 'Aguardando',
  ja_existia: 'Já existia',
  enviando: 'Enviando…',
  lancada: 'Lançada',
  falhou: 'Falhou',
};

const ImportarBorderoModal: React.FC<Props> = ({ isOpen, arquivoInicial, agrupamentos, ucsDoAgrupamento, onClose, onConcluido }) => {
  const [etapa, setEtapa] = useState<Etapa>('escolher');
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [separacao, setSeparacao] = useState<SeparacaoBordero | null>(null);
  const [envios, setEnvios] = useState<Record<number, ResultadoEnvio>>({});
  const [ausentes, setAusentes] = useState<UcAusente[]>([]);
  const [webhookUrl, setWebhookUrl] = useState<string | null>(null);
  const pararRef = useRef(false);
  const backdropMouseDownRef = useRef(false);

  /** Volta para a escolha do arquivo sem fechar: é a nova tentativa de envio. */
  const recomecar = useCallback((inicial: File | null = null) => {
    setEtapa('escolher');
    setArquivo(inicial);
    setErro(null);
    setSeparacao(null);
    setEnvios({});
    setAusentes([]);
    pararRef.current = false;
  }, []);

  useEffect(() => {
    if (isOpen) recomecar(arquivoInicial ?? null);
  }, [isOpen, arquivoInicial, recomecar]);

  // Mesmo webhook do "Importar fatura em PDF".
  useEffect(() => {
    if (!isOpen || webhookUrl) return;
    supabase.from('system_settings').select('value').eq('key', 'pdf_invoice_webhook_url').single()
      .then(({ data }) => { if (data?.value) setWebhookUrl(data.value); });
  }, [isOpen, webhookUrl]);

  const paginasLancar = useMemo(() => (separacao?.paginas ?? []).filter((p) => p.destino === 'lancar'), [separacao]);
  const identificacao = useMemo(() => (separacao ? identificarAgrupamento(separacao.paginas) : null), [separacao]);
  const codigosDoIdentificado = identificacao?.principal
    ? agrupamentos?.find((a) => a.id === identificacao.principal!.id)?.codigos ?? []
    : [];
  const mesesDoArquivo = useMemo(
    () => [...new Set((separacao?.paginas ?? []).map((p) => p.mes_referencia).filter(Boolean))] as string[],
    [separacao],
  );
  const ausentesSemFatura = ausentes.filter((u) => !u.comFatura);
  const resumoEnvio = useMemo(() => resumoDoEnvio(paginasLancar.map((p) => envios[p.ordem] ?? { estado: 'aguardando' })), [paginasLancar, envios]);
  const aEnviar = paginasLancar.filter((p) => envios[p.ordem]?.estado === 'aguardando');
  const comFalha = paginasLancar.filter((p) => envios[p.ordem]?.estado === 'falhou');
  const processados = resumoEnvio.lancadas + resumoEnvio.ja_existiam + resumoEnvio.falharam;

  const marcar = (ordem: number, r: ResultadoEnvio) => setEnvios((atual) => ({ ...atual, [ordem]: r }));

  const separar = async () => {
    if (!arquivo) return;
    setErro(null);
    setEtapa('separando');
    try {
      const corpo = new FormData();
      corpo.append('arquivo', arquivo);
      const { data, error } = await supabase.functions.invoke('separar-bordero', { body: corpo });
      if (error) {
        // functions.invoke esconde o motivo em error.context (ver CLAUDE.md).
        const detalhe = await (error as any)?.context?.json?.().catch(() => null);
        throw new Error(detalhe?.error || error.message);
      }
      if (!data?.ok) throw new Error(data?.error || 'Não foi possível separar o borderô.');
      const resultado = data as SeparacaoBordero;

      // UCs do agrupamento identificado que não têm página no arquivo: a fatura delas não está
      // no borderô, e a tela precisa dizer isso em vez de parecer falha do envio.
      const principal = identificarAgrupamento(resultado.paginas).principal;
      const foraDoArquivo = principal && ucsDoAgrupamento ? ucsForaDoBordero(ucsDoAgrupamento(principal.id), resultado.paginas) : [];
      const numerosDe = (u: UcDoAgrupamento) => [normUc(u.numero_uc), normUc(u.outro_numero_uc)].filter(Boolean);

      // Página cuja UC já tem fatura no mês nem vai para a leitura.
      const lancar = resultado.paginas.filter((p) => p.destino === 'lancar');
      const ucs = [...new Set([...lancar.map((p) => p.uc_norm).filter(Boolean) as string[], ...foraDoArquivo.flatMap(numerosDe)])];
      const meses = [...new Set(resultado.paginas.map((p) => p.mes_referencia).filter(Boolean))] as string[];
      let existentes = new Set<string>();
      if (ucs.length && meses.length) {
        const { data: faturas, error: errFat } = await supabase
          .from('faturas')
          .select('uc_codigo_norm, mes_referencia, classificacao, status_fatura')
          .in('uc_codigo_norm', ucs)
          .in('mes_referencia', meses);
        if (errFat) throw new Error(`Não foi possível conferir as faturas já lançadas: ${errFat.message}`);
        existentes = chavesComFatura(faturas ?? []);
      }
      const inicial: Record<number, ResultadoEnvio> = {};
      for (const p of lancar) {
        inicial[p.ordem] = existentes.has(chaveUcMes(p.uc_norm, p.mes_referencia))
          ? { estado: 'ja_existia', mensagem: 'Já havia fatura desta UC no mês.' }
          : { estado: 'aguardando' };
      }
      setEnvios(inicial);
      setAusentes(foraDoArquivo.map((u) => ({
        numero: u.numero_uc,
        comFatura: numerosDe(u).some((n) => meses.some((m) => existentes.has(chaveUcMes(n, m)))),
      })));
      setSeparacao(resultado);
      setEtapa('previa');
    } catch (e: any) {
      setErro(e?.message || 'Não foi possível separar o borderô.');
      setEtapa('escolher');
    }
  };

  const enviarPagina = useCallback(async (p: PaginaBordero) => {
    if (!webhookUrl) return;
    marcar(p.ordem, { estado: 'enviando' });
    try {
      const baixado = await fetch(p.url);
      if (!baixado.ok) throw new Error('Não foi possível baixar a página separada.');
      const corpo = new FormData();
      corpo.append('file', new File([await baixado.blob()], p.arquivo_nome, { type: 'application/pdf' }));

      const controle = new AbortController();
      const relogio = setTimeout(() => controle.abort(), TEMPO_MAXIMO_ROBO_MS);
      let resposta: Response;
      try {
        resposta = await fetch(webhookUrl, { method: 'POST', body: corpo, signal: controle.signal });
      } finally {
        clearTimeout(relogio);
      }
      const json = await resposta.json().catch(() => ({}));
      const leitura = interpretarRespostaRobo({ ok: resposta.ok, status: resposta.status }, json);

      // Resposta boa do n8n não prova nada: confere se a fatura entrou.
      let apareceu = false;
      if (leitura.tipo === 'ok') {
        const { data } = await supabase
          .from('faturas')
          .select('uc_codigo_norm, mes_referencia, classificacao, status_fatura')
          .eq('uc_codigo_norm', p.uc_norm)
          .eq('mes_referencia', p.mes_referencia);
        apareceu = chavesComFatura(data ?? []).size > 0;
      }
      marcar(p.ordem, resultadoDoEnvio(leitura, apareceu));
    } catch (e: any) {
      marcar(p.ordem, {
        estado: 'falhou',
        mensagem: e?.name === 'AbortError' ? 'O robô não respondeu em 3 minutos.' : e?.message || 'Falha no envio.',
      });
    }
  }, [webhookUrl]);

  const lancar = async (paginas: PaginaBordero[]) => {
    if (!paginas.length) return;
    pararRef.current = false;
    setEtapa('lancando');
    for (const p of paginas) marcar(p.ordem, { estado: 'aguardando' });
    await emFila(paginas, ENVIOS_SIMULTANEOS, enviarPagina, () => pararRef.current);
    setEtapa('concluido');
    onConcluido?.();
  };

  const fechar = () => {
    if (etapa === 'separando' || etapa === 'lancando') return;
    onClose();
  };

  if (!isOpen) return null;

  const resumo = separacao?.resumo;
  const temLista = !!(separacao && resumo && (etapa === 'previa' || etapa === 'lancando' || etapa === 'concluido'));
  const botaoSecundario = 'px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg font-medium transition-colors';
  const botaoPrimario = 'flex items-center gap-2 px-6 py-2 bg-primary hover:bg-primary-dark text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed';

  // Rodapé fixo: os botões ficam sempre à vista, só a lista de faturas rola.
  const rodape = (() => {
    if (etapa === 'escolher') {
      return (
        <>
          <button onClick={fechar} className={botaoSecundario}>Cancelar</button>
          <button onClick={separar} disabled={!arquivo} className={botaoPrimario}>
            <Layers size={18} />
            Separar faturas
          </button>
        </>
      );
    }
    if (etapa === 'previa') {
      return (
        <>
          <button onClick={fechar} className={botaoSecundario}>Cancelar</button>
          <button onClick={() => recomecar()} className={`flex items-center gap-2 ${botaoSecundario}`}>
            <RotateCcw size={16} />
            Enviar outro arquivo
          </button>
          <button onClick={() => lancar(aEnviar)} disabled={!aEnviar.length || !webhookUrl} className={botaoPrimario}>
            <Upload size={18} />
            {aEnviar.length === 1 ? 'Lançar 1 fatura' : `Lançar ${aEnviar.length} faturas`}
          </button>
        </>
      );
    }
    if (etapa === 'lancando') {
      return (
        <button onClick={() => { pararRef.current = true; }} className={`flex items-center gap-2 ${botaoSecundario}`}>
          <Square size={16} />
          Parar depois das que estão em envio
        </button>
      );
    }
    if (etapa === 'concluido') {
      return (
        <>
          {(comFalha.length > 0 || aEnviar.length > 0) && (
            <button
              onClick={() => lancar([...comFalha, ...aEnviar])}
              className="flex items-center gap-2 px-4 py-2 text-primary hover:bg-primary/10 rounded-lg font-medium transition-colors"
            >
              <RotateCcw size={16} />
              Tentar de novo ({comFalha.length + aEnviar.length})
            </button>
          )}
          <button onClick={() => recomecar()} className={`flex items-center gap-2 ${botaoSecundario}`}>
            <Upload size={16} />
            Enviar outro arquivo
          </button>
          <button onClick={fechar} className={botaoPrimario}>
            <CheckCircle2 size={18} />
            Fechar
          </button>
        </>
      );
    }
    return null; // separando: nada a fazer além de esperar
  })();

  return createPortal(
    <div
      className="fixed inset-0 z-[110] flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in p-4"
      onMouseDown={(e) => { backdropMouseDownRef.current = e.target === e.currentTarget; }}
      onClick={() => { if (backdropMouseDownRef.current) fechar(); backdropMouseDownRef.current = false; }}
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden animate-scale-in"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="shrink-0 flex items-center justify-between p-6 border-b border-gray-100 dark:border-gray-700">
          <div>
            <h2 className="text-xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
              <Layers className="text-primary" />
              Importar borderô de agrupamento
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              O arquivo .7z que a Equatorial manda por e-mail, ou um PDF com várias faturas (uma por página).
            </p>
          </div>
          {etapa !== 'separando' && etapa !== 'lancando' && (
            <button onClick={fechar} aria-label="Fechar" className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
              <X size={24} />
            </button>
          )}
        </div>

        {/* Só a tabela rola; o resto do corpo encolhe junto. Em tela muito baixa, onde nem a
            tabela mínima cabe, o corpo inteiro rola — o rodapé continua fixo. */}
        <div className="flex-1 min-h-0 overflow-y-auto p-6 flex flex-col gap-5">
          {etapa === 'escolher' && (
            <>
              <div className="shrink-0 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl p-8 flex flex-col items-center justify-center bg-gray-50 dark:bg-gray-900/50 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors relative cursor-pointer">
                <input
                  type="file"
                  accept=".7z,application/x-7z-compressed,application/pdf"
                  aria-label="Arquivo do borderô"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (!f) return;
                    if (!ehArquivoAceito(f)) { setErro('Selecione o arquivo .7z do borderô ou um PDF.'); setArquivo(null); return; }
                    setErro(null);
                    setArquivo(f);
                  }}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                />
                <div className="p-4 bg-primary/10 dark:bg-primary/20 rounded-full text-primary dark:text-primary-light mb-4">
                  <Upload size={32} />
                </div>
                {arquivo ? (
                  <div className="text-center">
                    <p className="font-medium text-gray-800 dark:text-gray-200">{arquivo.name}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{(arquivo.size / 1024 / 1024).toFixed(2)} MB</p>
                  </div>
                ) : (
                  <div className="text-center">
                    <p className="font-medium text-gray-700 dark:text-gray-300">Clique para selecionar o arquivo</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">faturas_bordero.7z ou PDF</p>
                  </div>
                )}
              </div>

              {erro && (
                <div className="shrink-0 p-3 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg flex items-center gap-2 text-sm border border-red-100 dark:border-red-800">
                  <AlertCircle size={16} className="shrink-0" />
                  <p>{erro}</p>
                </div>
              )}

              <p className="shrink-0 text-xs text-gray-500 dark:text-gray-400">
                Pode enviar o mesmo arquivo mais de uma vez: as faturas já lançadas são reconhecidas e só as que faltam vão para a leitura.
              </p>
            </>
          )}

          {etapa === 'separando' && (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Loader2 size={40} className="animate-spin text-primary" />
              <p className="mt-4 text-lg font-medium text-gray-700 dark:text-gray-200">Abrindo o arquivo e separando as faturas…</p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Leva alguns segundos.</p>
            </div>
          )}

          {temLista && separacao && resumo && (
            <>
              {identificacao && (
                <div className="shrink-0 rounded-xl border border-primary/30 bg-primary/5 dark:bg-primary/10 p-4" role="status">
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Agrupamento identificado</p>
                  {identificacao.principal ? (
                    <>
                      <p className="mt-1 flex flex-wrap items-baseline gap-x-2 text-lg font-bold text-gray-800 dark:text-gray-100">
                        {identificacao.principal.nome}
                        {codigosDoIdentificado.length > 0 && (
                          <span className="text-xs font-medium text-gray-500 dark:text-gray-400">cód. {codigosDoIdentificado.join(', ')}</span>
                        )}
                        {mesesDoArquivo.length > 0 && (
                          <span className="text-xs font-medium text-gray-500 dark:text-gray-400">{mesesDoArquivo.join(', ')}</span>
                        )}
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-300">
                        {identificacao.principal.faturas} de {identificacao.faturasComUc} UC(s) do arquivo estão neste agrupamento
                        {identificacao.foraDeAgrupamento > 0 && `; ${identificacao.foraDeAgrupamento} estão fora do rateio da Vigor`}.
                      </p>
                      {identificacao.outros.length > 0 && (
                        <p className="mt-1 text-sm text-amber-700 dark:text-secondary">
                          O arquivo também traz UCs de {identificacao.outros.map((o) => `${o.nome} (${o.faturas})`).join(', ')}: confira o cadastro dos agrupamentos.
                        </p>
                      )}
                    </>
                  ) : (
                    <p className="mt-1 text-sm text-amber-700 dark:text-secondary">
                      Nenhuma UC do arquivo está em um agrupamento da Vigor, então nada será lançado. Confira se as UCs estão atribuídas ao agrupamento certo.
                    </p>
                  )}
                </div>
              )}

              {ausentesSemFatura.length > 0 && (
                <div className="shrink-0 p-3 bg-yellow-50 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-500 rounded-lg flex items-start gap-2 text-sm border border-yellow-200 dark:border-yellow-800/30">
                  <AlertCircle size={16} className="shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold">
                      {ausentesSemFatura.length === 1
                        ? '1 UC deste agrupamento não veio no arquivo e está sem fatura no mês:'
                        : `${ausentesSemFatura.length} UCs deste agrupamento não vieram no arquivo e estão sem fatura no mês:`}{' '}
                      <span className="font-mono font-medium">
                        {ausentesSemFatura.slice(0, MAX_AUSENTES_NA_TELA).map((u) => u.numero).join(', ')}
                        {ausentesSemFatura.length > MAX_AUSENTES_NA_TELA && ` e mais ${ausentesSemFatura.length - MAX_AUSENTES_NA_TELA}`}
                      </span>
                    </p>
                    <p className="mt-1 text-xs">
                      A fatura {ausentesSemFatura.length === 1 ? 'dela' : 'delas'} não está neste borderô, então enviar o mesmo arquivo de novo não resolve.
                      Importe a fatura individual em Faturas e Cobranças (Importar fatura em PDF) ou dispense a UC do mês no Acompanhamento mensal.
                    </p>
                  </div>
                </div>
              )}

              <div className="shrink-0 grid grid-cols-2 sm:grid-cols-5 gap-3">
                {[
                  { rotulo: 'Faturas no arquivo', valor: String(resumo.faturas) },
                  { rotulo: 'Para lançar', valor: String(resumo.lancar) },
                  { rotulo: 'Para acompanhar', valor: String(resumo.acompanhamento) },
                  { rotulo: 'Para revisar', valor: String(resumo.revisar) },
                  { rotulo: 'Total das faturas', valor: reais(resumo.total_reais) },
                ].map((c) => (
                  <div key={c.rotulo} className="rounded-xl border border-gray-100 dark:border-gray-700 p-3">
                    <p className="text-xs text-gray-500 dark:text-gray-400">{c.rotulo}</p>
                    <p className="text-lg font-bold text-gray-800 dark:text-gray-100">{c.valor}</p>
                  </div>
                ))}
              </div>

              {resumo.em_branco > 0 && (
                <p className="shrink-0 text-xs text-gray-500 dark:text-gray-400 -mt-2">
                  {resumo.em_branco === 1 ? '1 página em branco foi ignorada.' : `${resumo.em_branco} páginas em branco foram ignoradas.`}
                </p>
              )}

              {(etapa === 'lancando' || etapa === 'concluido') && (
                <div className="shrink-0 rounded-xl bg-gray-50 dark:bg-gray-900/50 p-4">
                  <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
                    <span className="font-medium text-gray-700 dark:text-gray-200">
                      {etapa === 'lancando' ? `Lançando… ${processados} de ${paginasLancar.length}` : 'Envio terminado'}
                    </span>
                    <span className="text-gray-500 dark:text-gray-400">
                      {resumoEnvio.lancadas} lançada(s) · {resumoEnvio.ja_existiam} já existia(m) · {resumoEnvio.falharam} com falha
                    </span>
                  </div>
                  <div className="mt-2 h-2 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
                    <div
                      className="h-full bg-primary transition-all"
                      style={{ width: `${paginasLancar.length ? Math.round((processados / paginasLancar.length) * 100) : 0}%` }}
                    />
                  </div>
                </div>
              )}

              <div className="flex-1 min-h-[12rem] overflow-auto rounded-xl border border-gray-100 dark:border-gray-700">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 z-10 bg-gray-50 dark:bg-gray-900 text-left text-xs text-gray-500 dark:text-gray-400">
                    <tr>
                      <th className="px-3 py-2 font-medium">Pág.</th>
                      <th className="px-3 py-2 font-medium">UC</th>
                      <th className="px-3 py-2 font-medium">Mês</th>
                      <th className="px-3 py-2 font-medium text-right">Valor</th>
                      <th className="px-3 py-2 font-medium">Destino</th>
                      <th className="px-3 py-2 font-medium">Situação</th>
                      <th className="px-3 py-2"><span className="sr-only">PDF</span></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {separacao.paginas.map((p) => {
                      const envio = envios[p.ordem];
                      const detalhe = p.destino === 'lancar'
                        ? [envio ? ROTULO_ENVIO[envio.estado] : null, envio?.mensagem].filter(Boolean).join(': ')
                        : p.motivo;
                      const corSituacao = envio?.estado === 'falhou' ? 'text-red-600 dark:text-red-400'
                        : envio?.estado === 'lancada' ? 'text-emerald-600 dark:text-emerald-400'
                          : 'text-gray-600 dark:text-gray-300';
                      return (
                        <tr key={p.ordem} className="align-top">
                          <td className="px-3 py-2 text-gray-500 dark:text-gray-400 whitespace-nowrap">{rotuloPaginas(p)}</td>
                          <td className="px-3 py-2 font-medium text-gray-800 dark:text-gray-100 whitespace-nowrap">{p.uc ?? '—'}</td>
                          <td className="px-3 py-2 text-gray-600 dark:text-gray-300">{p.mes_referencia ?? '—'}</td>
                          <td className="px-3 py-2 text-right text-gray-800 dark:text-gray-100 whitespace-nowrap">{reais(p.total)}</td>
                          <td className="px-3 py-2">
                            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${ROTULO_DESTINO[p.destino].classe}`}>
                              {ROTULO_DESTINO[p.destino].texto}
                            </span>
                          </td>
                          <td className={`px-3 py-2 ${corSituacao}`}>
                            <span className="inline-flex items-center gap-1">
                              {envio?.estado === 'enviando' && <Loader2 size={12} className="animate-spin" />}
                              {detalhe || '—'}
                            </span>
                            {p.avisos.map((a) => (
                              <p key={a} className="text-xs text-amber-700 dark:text-secondary mt-0.5">{a}</p>
                            ))}
                          </td>
                          <td className="px-3 py-2">
                            <a href={p.url} target="_blank" rel="noreferrer" aria-label={`Abrir a fatura da página ${rotuloPaginas(p)}`} className="text-gray-400 hover:text-primary">
                              <ExternalLink size={14} />
                            </a>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {resumo.acompanhamento > 0 && (
                <p className="shrink-0 text-xs text-gray-500 dark:text-gray-400 -mt-2">
                  As UCs para acompanhar estão no agrupamento da Prefeitura, mas fora do rateio da Vigor: não viram fatura de cobrança.
                </p>
              )}

              {!webhookUrl && (
                <div className="shrink-0 p-3 bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-500 rounded-lg flex items-center gap-2 text-sm border border-yellow-200 dark:border-yellow-800/30">
                  <AlertCircle size={16} className="shrink-0" />
                  <p>A URL do webhook de importação não está configurada.</p>
                </div>
              )}
            </>
          )}
        </div>

        {rodape && (
          <div className="shrink-0 flex flex-wrap justify-end gap-3 px-6 py-4 border-t border-gray-100 dark:border-gray-700">
            {rodape}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
};

export default ImportarBorderoModal;
